<template>
  <div class="form-group">
    <label :for="name">
      {{ label ? t(`labels.${label}`) : t(`labels.${name}`) }}
    </label>

    <div ref="wrapperRef" class="relative">
      <InputGroup
        :class="[
          {
            'dirty-state-wrapper': isFieldDirty && !disabled,
            'error-state-wrapper': showError,
          },
        ]"
      >
        <!-- Placeholder Source Icon (if placeholderSource is set) -->
        <PlaceholderSourceButton
          :placeholderSource="props.placeholderSource"
          :placeholder="datePlaceholder"
          :currentValue="displayValue"
          :isVisible="isPlaceholderDifferent"
          :disabled="disabled"
          @accept="handleAcceptPlaceholder"
        />

        <InputText
          :id="name"
          ref="inputRef"
          v-model="displayValue"
          inputmode="numeric"
          :placeholder="datePlaceholder"
          :disabled="disabled"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
          @keydown="onKeyDown"
          @dblclick="onPlaceholderDblClick"
        />
        <Button
          v-if="!disabled"
          type="button"
          icon="pi pi-calendar"
          :disabled="disabled"
          tabindex="-1"
          :class="[
            'p-inputgroup-addon !my-0',
            showCalendar ? 'p-button-primary' : 'p-button-text p-button-plain',
          ]"
          @click="toggleCalendar"
        />
      </InputGroup>

      <transition name="fade">
        <div v-if="showCalendar" class="absolute left-0 z-20 mt-1 w-full">
          <DatePicker
            v-model="calendarDate"
            class="w-full"
            inline
            :locale="locale"
            :minDate="parsedMinDate ?? undefined"
            :maxDate="parsedMaxDate ?? undefined"
            @update:model-value="onCalendarSelect"
          />
        </div>
      </transition>
      <div v-if="showError && combinedErrorMessage" class="absolute w-full -ml-2 py-1">
        <FormError :message="combinedErrorMessage" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import InputGroup from 'primevue/inputgroup';
import InputText from 'primevue/inputtext';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type { InvoiceItemSuggestionSource } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import {
  dateToDateOnlyString,
  dateToSafeUtcIso,
  formatDate,
  normalizeBackendIsoToDateOnlyString,
  parseBackendUtcDate,
  parseISODate,
} from '@/utils/dateUtils';

import PlaceholderSourceButton from './elements/PlaceholderSourceButton.vue';
import FormError from './errors/FormError.vue';

const props = defineProps<{
  name: string;
  modelValue: string | undefined;
  initialValue: string | undefined;
  placeholder?: string;
  isDirty: (dirty: boolean) => void;
  label?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  acceptPlaceholderOnDblClick?: boolean;
  placeholderSource?: InvoiceItemSuggestionSource;
  onAcceptPlaceholder?: () => void;
  acceptsPlaceholderData?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
}>();

const { t, locale } = useI18n();

// validation / error handling
const { errorMessage, validate, meta } = useField<Date>(props.name);
const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const isFieldDirty = ref(false);

const displayValue = ref<string>('');
const internalDate = ref<Date | null>(null);
const isEditing = ref(false);
const hasUserInteracted = ref(false);

const showCalendar = ref(false);
const calendarDate = ref<Date | null>(null);

const wrapperRef = ref<HTMLDivElement | null>(null);
const inputRef = ref<ComponentPublicInstance | null>(null);
const adjustedWarning = ref<string | null>(null);

const parsedMinDate = computed(() =>
  props.minDate ? (parseBackendUtcDate(props.minDate) ?? undefined) : undefined,
);

const parsedMaxDate = computed(() =>
  props.maxDate ? (parseBackendUtcDate(props.maxDate) ?? undefined) : undefined,
);

const combinedErrorMessage = computed(() => {
  return adjustedWarning.value || errorMessage.value || '';
});

/**
 * Compute the current value as ISO date string (date-only format)
 * Used for comparing with placeholder to determine button visibility
 */
const currentIsoValue = computed(() => {
  if (!internalDate.value) return null;
  const d = internalDate.value;
  const yyyy = String(d.getFullYear()).padStart(4, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
});

/**
 * Normalize placeholder to ISO date string (date-only format)
 * Handles backend UTC format and plain ISO dates
 */
const placeholderIsoValue = computed(() => {
  if (!props.placeholder) return null;
  const raw = props.placeholder.trim();
  if (!raw) return null;

  const parsed =
    parseBackendUtcDate(raw) ??
    parseISODate(raw) ??
    (Number.isFinite(Date.parse(raw)) ? new Date(raw) : null);

  if (!parsed) return null;

  const d = parsed;
  const yyyy = String(d.getFullYear()).padStart(4, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
});

/**
 * Button should be visible only if current value differs from placeholder
 */
const isPlaceholderDifferent = computed(() => {
  if (!props.placeholderSource || !placeholderIsoValue.value) return false;
  return currentIsoValue.value !== placeholderIsoValue.value;
});

const datePlaceholder = computed(() => {
  if (props.disabled) {
    return ''; // no placeholder in view mode
  }

  // If a placeholder prop is explicitly provided, prefer it
  const raw = props.placeholder?.trim();
  if (raw && raw.length > 0) {
    // Try to interpret placeholder as ISO date string
    // 1) Backend-UTC-Parser (z. B. "2023-01-01T00:00:00.000Z")
    // 2) Plain ISO-Date-Parser (z. B. "2023-01-01")
    // 3) Fallback: native Date.parse
    const parsed =
      parseBackendUtcDate(raw) ??
      parseISODate(raw) ??
      (Number.isFinite(Date.parse(raw)) ? new Date(raw) : null);

    if (parsed) {
      // Format exactly as a "real" value would be displayed
      return formatLong(parsed);
    }

    // Not a date → use raw placeholder text
    return raw;
  }

  const sep = locale.value.startsWith('en') ? '/' : '.';

  // German: TT.MM.JJJJ
  if (locale.value.startsWith('de')) {
    return `TT${sep}MM${sep}JJJJ`;
  }

  // Default / English: DD/MM/YYYY
  return `DD${sep}MM${sep}YYYY`;
});

const initialIso = computed(() => props.initialValue ?? null);

const initialShort = computed(() => {
  if (!initialIso.value) return '';
  const d = parseISODate(initialIso.value);
  return d ? formatShort(d) : '';
});

function formatShort(date: Date | null): string {
  if (!date) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear());

  // only the separator depends on locale, not the order
  const sep = locale.value.startsWith('en') ? '/' : '.';

  return `${dd}${sep}${mm}${sep}${yyyy}`;
}

function formatLong(date: Date | null): string {
  if (!date) return '';
  return formatDate(date, locale.value);
}

function cleanUserInput(raw: string, sep: string): string {
  // choose the "other" separator, which we want to drop
  const otherSep = sep === '.' ? '/' : '.';

  let filtered = '';

  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      // keep digits
      filtered += ch;
    } else if (ch === sep) {
      // keep only the active separator
      filtered += sep;
    } else if (ch === otherSep) {
      // drop the non-active separator completely
      // (user might have typed the "wrong" one)
      continue;
    } else {
      // drop any other characters (letters, spaces, etc.)
      continue;
    }
  }

  // collapse multiple separators → only a single one remains
  const escapedSep = sep === '.' ? '\\.' : '/';
  const multiSepRegex = new RegExp(`${escapedSep}{2,}`, 'g');
  filtered = filtered.replace(multiSepRegex, sep);

  return filtered;
}

/**
 * If the cleaned input currently ends with a separator, we try to be nice
 * to “fast typers”:
 * - Segment 0 (day): if it has exactly 1 digit and not "0", pad to 2 digits → "4." -> "04."
 * - Segment 1 (month): same rule → "04.4." -> "04.04."
 * Everything else stays untouched.
 */
function padSingleDigitSegmentsForTrailingSep(cleaned: string, sep: string): string {
  if (!cleaned.endsWith(sep)) {
    return cleaned;
  }

  // Remove trailing separator temporarily
  const withoutTrailing = cleaned.slice(0, -1);

  // Split into at most 3 parts (day, month, yearFragment)
  const parts = withoutTrailing.split(sep).slice(0, 3);

  // Day segment (index 0)
  if (parts[0] && parts[0].length === 1 && parts[0] !== '0') {
    parts[0] = parts[0].padStart(2, '0'); // "4" -> "04"
  }

  // Month segment (index 1)
  if (parts.length > 1 && parts[1] && parts[1].length === 1 && parts[1] !== '0') {
    parts[1] = parts[1].padStart(2, '0'); // "4" -> "04"
  }

  // Rebuild and re-append trailing separator
  const rebuilt = parts.join(sep);
  return rebuilt + sep;
}

/**
 * When the input already has separators, we can immediately enforce basic
 * day/month plausibility:
 * - Segment 0 = day  → clamp to 1..31 and pad to 2 digits
 * - Segment 1 = month → clamp to 1..12 and pad to 2 digits
 *   After we know the month, we also clamp the day to the maximum valid day
 *   for that month (ignoring leap year details; February → max 29).
 *
 * This runs while typing, right after the user typed a separator.
 */
function clampDayAndMonthForSeparatedInput(cleaned: string, sep: string): string {
  if (!cleaned.includes(sep)) return cleaned;

  const endsWithSep = cleaned.endsWith(sep);
  const withoutTrailing = endsWithSep ? cleaned.slice(0, -1) : cleaned;

  const parts = withoutTrailing.split(sep).slice(0, 3); // [day, month, yearFragment?]

  // --- Day segment (index 0) ---
  if (parts[0]) {
    let d = Number(parts[0]);
    if (Number.isFinite(d)) {
      if (d < 1) d = 1;
      if (d > 31) d = 31;
      parts[0] = String(d).padStart(2, '0'); // always 2 digits
    }
  }

  // --- Month segment (index 1) ---
  if (parts.length > 1 && parts[1]) {
    let m = Number(parts[1]);
    if (Number.isFinite(m)) {
      if (m < 1) m = 1;
      if (m > 12) m = 12;
      parts[1] = String(m).padStart(2, '0'); // always 2 digits

      // After we know the month, adjust day to the max valid day for this month
      if (parts[0]) {
        let d = Number(parts[0]);
        if (Number.isFinite(d)) {
          // Simple month/day limits; ignore real leap-year, allow up to 29 for February
          let maxDay = 31;
          if (m === 2) {
            maxDay = 29;
          } else if ([4, 6, 9, 11].includes(m)) {
            maxDay = 30;
          }

          if (d < 1) d = 1;
          if (d > maxDay) d = maxDay;
          parts[0] = String(d).padStart(2, '0');
        }
      }
    }
  }

  const rebuilt = parts.join(sep);
  return endsWithSep ? rebuilt + sep : rebuilt;
}

/**
 * Clamp month and day for a given year:
 * - month is normalized to 1..12
 * - day is normalized to 1..last day of that month/year
 */
function clampDayMonthForYear(
  day: number,
  month: number,
  year: number,
): { day: number; month: number } {
  // normalize month
  if (!Number.isFinite(month)) month = 1;
  if (month < 1) month = 1;
  if (month > 12) month = 12;

  // normalize day
  if (!Number.isFinite(day)) day = 1;

  const daysInMonth = new Date(year, month, 0).getDate(); // 0th = last of previous month
  if (day < 1) day = 1;
  if (day > daysInMonth) day = daysInMonth;

  return { day, month };
}

function onKeyDown(e: KeyboardEvent) {
  // Handle Enter explicitly: commit value + trigger validation
  if (e.key === 'Enter') {
    // prevent default form submit
    e.preventDefault();

    // 1) commit current value (parse, clamp, emit)
    if (isEditing.value) {
      // This will parse/clamp/emit and also set isEditing.value = false
      onBlur();

      // 2) trigger vee-validate validation for this field
      void validate();
    }

    return;
  }

  // Allow navigation & control keys
  const controlKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Tab',
    'Home',
    'End',
  ];

  if (controlKeys.includes(e.key)) {
    return; // allow
  }

  // Allow common shortcuts (Ctrl/Cmd + A/C/V/X/Z/Y etc.)
  if (e.ctrlKey || e.metaKey) {
    return;
  }

  // Active separator for current locale
  const sep = locale.value.startsWith('en') ? '/' : '.';

  const isDigit = e.key >= '0' && e.key <= '9';
  const isSeparator = e.key === sep;

  if (isDigit || isSeparator) {
    return; // allowed characters
  }

  // Everything else is blocked
  e.preventDefault();
}

/**
 * Very simple masking:
 * - accepts up to 8 digits (ddmmyyyy)
 * - puts separator after day (2 digits) and month (next 2 digits)
 * - no double separators, max 2 separators
 * - no validation/clamping here – that happens on blur.
 */
function maskDateInput(digitsInput: string, sep: string): string {
  const digits = digitsInput.replace(/\D/g, '').slice(0, 8);
  const len = digits.length;

  if (len === 0) return '';

  if (len <= 2) {
    // typing day
    return digits;
  }

  if (len <= 4) {
    // dd + sep + month fragment (1–2 digits)
    const day = digits.slice(0, 2);
    const monthFragment = digits.slice(2); // 0–2 digits
    return monthFragment ? `${day}${sep}${monthFragment}` : day;
  }

  // len >= 5 → dd.sep.mm.sep.yearFragment (1–4 digits)
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const yearFragment = digits.slice(4); // 1–4 digits

  let out = `${day}${sep}${month}`;
  if (yearFragment.length > 0) {
    out += `${sep}${yearFragment}`;
  }

  return out;
}

function onFocus() {
  if (props.disabled) return;

  isEditing.value = true;
  adjustedWarning.value = null;

  // switch to short edit format
  if (internalDate.value) {
    displayValue.value = formatShort(internalDate.value);
  } else {
    displayValue.value = '';
  }

  // select all text after DOM update
  void nextTick(() => {
    // PrimeVue InputText wraps a native input
    const rootEl = inputRef.value?.$el as HTMLElement | undefined;
    const nativeInput =
      (rootEl?.querySelector('input') as HTMLInputElement | null) ??
      (rootEl as unknown as HTMLInputElement | null);

    nativeInput?.focus();
    nativeInput?.select(); // marks whole content of input
  });
}

function recomputeDirty() {
  if (!hasUserInteracted.value) return;

  const currentDateOnly = internalDate.value ? dateToDateOnlyString(internalDate.value) : null;

  const initialDateOnly = normalizeBackendIsoToDateOnlyString(initialIso.value);

  const isoDirty = currentDateOnly !== initialDateOnly;

  const currentDisplay = displayValue.value.trim();
  const initialDisplay = initialShort.value.trim();
  const displayDirty = currentDisplay !== initialDisplay;

  const dirty = isoDirty || displayDirty;

  isFieldDirty.value = dirty;
  props.isDirty(dirty);
}

function onInput(e: Event) {
  const target = e.target as HTMLInputElement | null;
  if (!target) return;

  hasUserInteracted.value = true;

  const sep = locale.value.startsWith('en') ? '/' : '.';

  // 1) read current text from input
  const raw = target.value ?? '';

  // remember if the user just typed a separator (any of "." or "/")
  const userEndedWithSep = raw.endsWith('.') || raw.endsWith('/');

  // 2) clean it:
  //    - keep only digits + active separator
  //    - remove the "wrong" separator
  //    - collapse duplicate separators
  const cleaned = cleanUserInput(raw, sep);

  // 3) if user just typed a separator, auto-pad 1-digit day/month segments
  const afterPad = userEndedWithSep ? padSingleDigitSegmentsForTrailingSep(cleaned, sep) : cleaned;

  // 4) if we have separators, and user just finished a segment with sep,
  //    clamp day/month ranges (day ≤ 31, month ≤ 12, day <= monthMax)
  const normalized = userEndedWithSep ? clampDayAndMonthForSeparatedInput(afterPad, sep) : afterPad;

  // 5) extract pure digits from normalized string
  const digits = normalized.replace(/\D/g, '');

  // 6) build masked string from digits (dd[sep]mm[sep]yyyy)
  let masked = maskDateInput(digits, sep);

  // 7) if the user ended with a separator but maskDateInput stripped it,
  //    re-append one separator to respect the user's intention
  if (userEndedWithSep && masked && !masked.endsWith(sep)) {
    masked += sep;
  }

  // 8) write back to state + native input
  displayValue.value = masked;
  target.value = masked;
}

/**
 * Expand year digits according to rules:
 * - 1 digit (e.g. "5")   -> 2005
 * - 2 digits (e.g. "55") -> 2055
 * - 3 digits (e.g. "555")-> 2555
 * - 4+ digits            -> first 4 digits as full year (e.g. "2024" -> 2024)
 */
function expandYearDigits(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;

  if (digits.length === 1) {
    return 2000 + Number(digits);
  }
  if (digits.length === 2 || digits.length === 3) {
    return 2000 + Number(digits);
  }
  // 4 or more digits → use first 4 as full year
  return Number(digits.slice(0, 4));
}

function onBlur() {
  isEditing.value = false;

  const raw = displayValue.value.trim();
  adjustedWarning.value = null;

  if (!raw) {
    // Empty input → clear value
    internalDate.value = null;
    emit('update:modelValue', null);
    showCalendar.value = false;
    return;
  }

  const hasSeparator = raw.includes('.') || raw.includes('/');

  let date: Date | null = null;

  if (hasSeparator) {
    // User gave explicit separators -> we respect them and coerce from parts
    date = coerceSeparatedToDate(raw);
  } else {
    // Pure digit input -> only allow guessing when we have at least 5 digits
    date = coerceDigitsToDate(raw);
  }

  if (!date) {
    // Could not build a valid/meaningful date:
    // - Do NOT change the internal model
    // - Reset visible value back to last valid date (if any)
    if (internalDate.value) {
      displayValue.value = formatLong(internalDate.value);
    }

    adjustedWarning.value = t('errors.validation.invalidDateInput');

    showCalendar.value = false;
    return;
  }

  // From here on we have a valid coerced Date
  hasUserInteracted.value = true;

  // Normalize to local date-only
  const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  internalDate.value = normalized;
  calendarDate.value = normalized;

  const iso = dateToSafeUtcIso(normalized); // noon UTC ISO
  emit('update:modelValue', iso);

  displayValue.value = formatLong(normalized);
  showCalendar.value = false;
}

/**
 * Coerce a pure digit input (no separators) to a Date.
 *
 * Rules:
 * - Only digits are used.
 * - If we have less than 5 digits → cannot decide day+month+year → return null.
 * - First 2 digits = day, next 2 digits = month, rest = year digits (1–n).
 * - Year digits are expanded via expandYearDigits().
 * - Then we clamp:
 *   - month to 1..12
 *   - day to 1..daysInMonth(year, month)
 * - If everything is numeric and year could be built → always return a Date.
 */
function coerceDigitsToDate(raw: string): Date | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 5) {
    // Not enough information for dd mm year
    return null;
  }

  const dayStr = digits.slice(0, 2);
  const monthStr = digits.slice(2, 4);
  const yearPart = digits.slice(4); // 1–4+ digits

  let day = Number(dayStr);
  let month = Number(monthStr);
  const year = expandYearDigits(yearPart);

  if (!Number.isFinite(day) || !Number.isFinite(month) || year == null) {
    return null;
  }

  const clamped = clampDayMonthForYear(day, month, year);
  day = clamped.day;
  month = clamped.month;

  return new Date(year, month - 1, day);
}

/**
 * Coerce separated input like "13.4.22" or "13/04/2022" to a Date.
 *
 * Rules:
 * - Split by "." or "/".
 * - First part = day, second part = month (1–2 digits each).
 * - Remaining parts combined = year digits (1–n) → expandYearDigits().
 * - Then clamp:
 *   - month 1..12
 *   - day 1..daysInMonth(year, month)
 * - If we cannot build a year or any numeric conversion fails → null.
 */
function coerceSeparatedToDate(raw: string): Date | null {
  const parts = raw
    .split(/[./]/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    // Need at least day and month
    return null;
  }

  let day = Number(parts[0].replace(/\D/g, ''));
  let month = Number(parts[1].replace(/\D/g, ''));
  const yearRaw = parts.slice(2).join(''); // combine remaining parts, may be empty

  if (!Number.isFinite(day) || !Number.isFinite(month)) {
    return null;
  }

  if (!yearRaw) {
    // No year → cannot build full date
    return null;
  }

  const year = expandYearDigits(yearRaw);
  if (year == null) return null;

  const clamped = clampDayMonthForYear(day, month, year);
  day = clamped.day;
  month = clamped.month;

  return new Date(year, month - 1, day);
}

function toggleCalendar() {
  if (props.disabled) return;
  showCalendar.value = !showCalendar.value;
}

function handleClickOutside(event: MouseEvent) {
  if (!showCalendar.value) return;
  const target = event.target as Node | null;
  const el = wrapperRef.value;
  if (!el || !target) return;

  if (!el.contains(target)) {
    showCalendar.value = false;
  }
}

function onCalendarSelect(newDate: Date | Date[] | (Date | null)[] | null | undefined) {
  if (!(newDate instanceof Date)) return;

  hasUserInteracted.value = true;

  // Normalize to date-only (strip time just in case)
  const normalized = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());

  internalDate.value = normalized;
  calendarDate.value = normalized;

  const iso = dateToSafeUtcIso(normalized); // "YYYY-MM-DDT12:00:00.000Z"
  emit('update:modelValue', iso);

  displayValue.value = formatLong(normalized);
  showCalendar.value = false;
}

function onPlaceholderDblClick() {
  // Disabled → nothing to do
  if (props.disabled) return;

  // Feature only active if prop is truthy
  if (!props.acceptPlaceholderOnDblClick) return;

  const raw = props.placeholder?.trim();
  if (!raw) return;

  // Try to parse placeholder as date suggestion
  const parsed =
    parseBackendUtcDate(raw) ??
    parseISODate(raw) ??
    (Number.isFinite(Date.parse(raw)) ? new Date(raw) : null);

  // If placeholder is not a date → simple text toggle fallback
  if (!parsed) {
    const current = displayValue.value.trim();

    // If current equals placeholder text → clear
    if (current === raw) {
      internalDate.value = null;
      calendarDate.value = null;
      displayValue.value = '';
      emit('update:modelValue', null);
      hasUserInteracted.value = true; // mark as user interaction for dirty state
      return;
    }

    // Else apply placeholder as plain string
    internalDate.value = null;
    calendarDate.value = null;
    displayValue.value = raw;
    emit('update:modelValue', raw);
    hasUserInteracted.value = true;
    return;
  }

  // --- Placeholder is a valid date ---

  // Normalize both dates to date-only string for comparison
  const placeholderDateOnly = dateToDateOnlyString(parsed);
  const currentDateOnly = internalDate.value ? dateToDateOnlyString(internalDate.value) : null;

  // If current date equals placeholder date → clear value
  if (currentDateOnly === placeholderDateOnly) {
    internalDate.value = null;
    calendarDate.value = null;
    displayValue.value = '';
    emit('update:modelValue', null);
    hasUserInteracted.value = true;
    return;
  }

  // Else: adopt placeholder date as actual value
  const normalized = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());

  internalDate.value = normalized;
  calendarDate.value = normalized;

  const iso = dateToSafeUtcIso(normalized); // normalized ISO string for the model
  emit('update:modelValue', iso);

  displayValue.value = formatLong(normalized);
  hasUserInteracted.value = true;
}

function handleAcceptPlaceholder() {
  // Icon-click to accept placeholder from specific source (ai / latest / other)
  if (props.disabled || !props.placeholderSource || !props.placeholder) return;

  const raw = props.placeholder.trim();
  if (!raw) return;

  // Try to parse placeholder as date suggestion
  const parsed =
    parseBackendUtcDate(raw) ??
    parseISODate(raw) ??
    (Number.isFinite(Date.parse(raw)) ? new Date(raw) : null);

  // If placeholder is not a date → simple text toggle fallback
  if (!parsed) {
    internalDate.value = null;
    calendarDate.value = null;
    displayValue.value = raw;
    emit('update:modelValue', raw);
    hasUserInteracted.value = true;

    // Call optional callback to notify parent
    props.onAcceptPlaceholder?.();
    return;
  }

  // --- Placeholder is a valid date ---
  const normalized = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());

  internalDate.value = normalized;
  calendarDate.value = normalized;

  const iso = dateToSafeUtcIso(normalized);
  emit('update:modelValue', iso);

  displayValue.value = formatLong(normalized);
  hasUserInteracted.value = true;

  // Call optional callback to notify parent
  props.onAcceptPlaceholder?.();
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      const d = parseBackendUtcDate(newVal);
      internalDate.value = d;
      calendarDate.value = d;
      displayValue.value = d ? (isEditing.value ? formatShort(d) : formatLong(d)) : '';
    } else {
      internalDate.value = null;
      displayValue.value = '';
      calendarDate.value = null;
    }

    recomputeDirty();
  },
  { immediate: true },
);

// internalDate changes (blur, calendar select, external modelValue change)
watch(
  () => internalDate.value,
  () => {
    recomputeDirty();
  },
  { immediate: true },
);

// displayValue changes (user typing)
watch(
  () => displayValue.value,
  () => {
    recomputeDirty();
  },
);

// initialValue changes (e.g. form reset)
watch(
  () => props.initialValue,
  () => {
    hasUserInteracted.value = false;
    isFieldDirty.value = false;
    props.isDirty(false);
    recomputeDirty();
  },
);

watch(
  () => locale.value,
  () => {
    if (!internalDate.value) return;
    displayValue.value = isEditing.value
      ? formatShort(internalDate.value) // on typing: short
      : formatLong(internalDate.value); // else: long
  },
);

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}
</style>
